import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellRing, X } from 'lucide-react';
interface DailyRewriteReminderProps {
  onDismiss?: () => void;
}
export function DailyRewriteReminder({
  onDismiss
}: DailyRewriteReminderProps) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    // Check if dismissed in this session
    const isDismissed = sessionStorage.getItem('rewrite_reminder_dismissed');
    if (!isDismissed) {
      // Small delay to let the page load first
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);
  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('rewrite_reminder_dismissed', 'true');
    if (onDismiss) onDismiss();
  };
  const handleScrollToInput = () => {
    const inputEl = document.getElementById('rewriter-input');
    if (inputEl) {
      inputEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // Optional: focus the textarea after scrolling
      setTimeout(() => {
        const textarea = inputEl.querySelector('textarea');
        if (textarea) textarea.focus();
      }, 500);
    }
  };
  return <AnimatePresence data-id="element-2166" data-mp="3cls1oq9pj0il">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: -20,
      height: 0
    }} animate={{
      opacity: 1,
      y: 0,
      height: 'auto'
    }} exit={{
      opacity: 0,
      y: -20,
      height: 0
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 25
    }} className="mb-8 overflow-hidden" data-id="element-2167" data-mp="37o071ohoh0ap">
          <div className="bg-gradient-to-r from-[#0a0a0a] to-[#111] border border-gray-800 rounded-xl p-4 relative group" data-id="element-2168" data-mp="2jye89257wn38">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-2169" data-mp="2fxc6adjx1wxk" />
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-2170" data-mp="25a5icgvliihc" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-2171" data-mp="2abl3sa1oqr09" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-2172" data-mp="1wcby44b3vfaj" />

            <button onClick={handleDismiss} className="absolute top-3 right-3 p-1.5 text-gray-500 hover:text-white hover:bg-white/5 rounded-md transition-colors" aria-label="Dismiss reminder" data-id="element-2173" data-mp="21awqjhk48uof">
              <X size={16} data-id="element-2174" data-mp="2f9wdmkrzd1ih" />
            </button>

            <div className="flex items-start sm:items-center gap-4 pr-8" data-id="element-2175" data-mp="2f93cq0fpd689">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF0000]/10 border border-[#FF0000]/20 flex items-center justify-center" data-id="element-2176" data-mp="2k868eu9sm03k">
                <BellRing className="w-5 h-5 text-[#FF0000]" data-id="element-2177" data-mp="37wh4zl8ghdm1" />
              </div>

              <div className="flex-1" data-id="element-2178" data-mp="3cqtofv9xmoxs">
                <h3 className="text-white font-bold text-sm mb-1 uppercase tracking-wide" data-id="element-2179" data-mp="3m42k88apyga9">
                  Daily Rewrite Reminder
                </h3>
                <p className="text-gray-400 text-xs font-mono leading-relaxed mb-2 sm:mb-0" data-id="element-2180" data-mp="3i1z7lgcbd5ne">
                  You have 3 free rewrites today. Practice healthier
                  communication — your words matter. 💬
                </p>
              </div>

              <div className="hidden sm:block flex-shrink-0" data-id="element-2181" data-mp="334yhfh9rtir1">
                <button onClick={handleScrollToInput} className="text-xs font-bold text-[#FF0000] hover:text-red-400 uppercase tracking-widest transition-colors" data-id="element-2182" data-mp="337atjdsukyw6">
                  Start Rewriting →
                </button>
              </div>
            </div>

            {/* Mobile CTA */}
            <div className="sm:hidden mt-3 pt-3 border-t border-gray-800" data-id="element-2183" data-mp="scnd6n8b7riq">
              <button onClick={handleScrollToInput} className="text-xs font-bold text-[#FF0000] hover:text-red-400 uppercase tracking-widest transition-colors w-full text-left" data-id="element-2184" data-mp="ne2eis8phfwe">
                Start Rewriting →
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}